import { useCallback, useEffect, useRef, useState } from "react";
import { CloudArrowUp, CheckCircle, Warning, CircleNotch } from "@phosphor-icons/react";
import { api, qty } from "./registerApi";

// 3 Nov 2026, user: closing the count squares SAP's godown with the physical
// figure - more on the floor than SAP holds goes back in as a cost centre
// return, less goes out as consumption. These are the documents it sent.
const LABEL = { issue: "consumption out of the godown", return: "put back into the godown" };

export const CountTrueUp = ({ weekId }) => {
  const [postings, setPostings] = useState([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!weekId) return;
    try {
      const all = await api.postings({ week_id: weekId });
      setPostings(all.filter((p) => p.kind === "count"));
    } catch (e) { /* the week itself still shows */ }
  }, [weekId]);

  useEffect(() => { load(); }, [load]);

  const pending = postings.some((p) => p.status === "pending");
  useEffect(() => {
    if (!pending) { if (timer.current) clearInterval(timer.current); return undefined; }
    timer.current = setInterval(load, 2500);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [pending, load]);

  if (postings.length === 0) return null;

  return (
    <div className="rounded-sm border border-[#D0D5DD] bg-white overflow-hidden" data-testid="count-trueup">
      <div className="px-3 py-2 bg-[#F9FAFB] border-b border-[#EAECF0] flex items-center gap-2">
        <CloudArrowUp size={15} weight="bold" className="text-[#0E7C86] shrink-0" />
        <span className="text-[13px] font-bold text-[#101828]">Closing count sent to SAP</span>
      </div>
      <div className="divide-y divide-[#EAECF0]">
        {postings.map((p) => (
          <div key={p.id} className="px-3 py-2 space-y-1" data-testid={`count-trueup-${p.direction}`}>
            <div className="flex items-center gap-1.5 text-[12px] font-bold">
              {p.status === "posted" && <CheckCircle size={14} weight="fill" className="text-[#027A48]" />}
              {p.status === "pending" && <CircleNotch size={14} className="animate-spin text-[#B54708]" />}
              {(p.status === "failed" || p.status === "unknown") && <Warning size={14} weight="bold" className="text-[#B42318]" />}
              <span className={p.status === "posted" ? "text-[#027A48]" : p.status === "pending" ? "text-[#B54708]" : "text-[#B42318]"}>
                {LABEL[p.direction]} · {p.status}
              </span>
              <span className="text-[11px] font-normal text-[#667085]">
                {p.document_date}{p.sap_gac_id ? ` · document ${p.sap_gac_id}` : ""}
              </span>
            </div>
            {p.error && <p className="text-[11px] text-[#B42318]" data-testid={`count-trueup-error-${p.direction}`}>{p.error}</p>}
            {p.lines.map((l) => (
              <div key={l.product_id} className="flex items-center justify-between gap-3 text-[12px]" data-testid={`count-trueup-line-${l.product_id}`}>
                <span className="font-mono font-bold text-[#101828] truncate">{l.product_id}</span>
                <span className="text-[11px] text-[#667085] shrink-0">
                  counted {qty(l.counted)} · SAP held {qty(l.sap_on_hand)}
                </span>
                <span className="tabular-nums font-bold text-[#1D2939] shrink-0">
                  {p.direction === "return" ? "+" : "−"}{qty(l.quantity)} {l.unit_code}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
